import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { useAuth } from '../contexts/AuthContext';
import useGoogleAuthFlow from '../hooks/useGoogleAuthFlow';
import apiClient from '../api/axios';

const VALID_PLANS = ['STARTER', 'GROWTH', 'ENTERPRISE'];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[0-9+\-\s()]{7,20}$/;
const PASSWORD_REGEX = /^(?=.*[A-Za-z])(?=.*\d).+$/;

const Spinner = () => (
  <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M1 12s4-7.5 11-7.5S23 12 23 12s-4 7.5-11 7.5S1 12 1 12Z" />
    <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const EyeOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.58 10.58a2 2 0 0 0 2.83 2.83M9.88 4.61A10.94 10.94 0 0 1 12 4.5c5 0 9.27 3.11 11 7.5a11.72 11.72 0 0 1-3.17 4.39M6.61 6.61A11.74 11.74 0 0 0 1 12c1.73 4.39 6 7.5 11 7.5a10.94 10.94 0 0 0 5.39-1.39" />
  </svg>
);

function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useAuth();
  const { t } = useTranslation(['signup', 'common']);

  const planParam = searchParams.get('plan')?.toUpperCase();
  const plan = VALID_PLANS.includes(planParam) ? planParam : null;

  useEffect(() => {
    if (!plan) {
      navigate('/pricing', { replace: true });
    }
  }, [plan, navigate]);

  const [trialDays, setTrialDays] = useState(null);
  useEffect(() => {
    apiClient.get('/api/public/pricing')
      .then((res) => setTrialDays(res.data.trialDays))
      .catch(() => {}); // badge just omits the trial-length text if this fails
  }, []);

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    restaurantName: '',
    password: '',
    confirmPassword: '',
    terms: false,
  });
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const { handleGoogleCredential, googleError, googleLoading } = useGoogleAuthFlow(plan);

  const getErrors = (f) => {
    const errs = {};
    if (!f.fullName.trim() || f.fullName.trim().length < 2)
      errs.fullName = t('errors.fullNameRequired');
    if (!f.email.trim())
      errs.email = t('errors.emailRequired');
    else if (!EMAIL_REGEX.test(f.email.trim()))
      errs.email = t('errors.emailInvalid');
    if (!f.phone.trim())
      errs.phone = t('errors.phoneRequired');
    else if (!PHONE_REGEX.test(f.phone.trim()))
      errs.phone = t('errors.phoneInvalid');
    if (!f.restaurantName.trim() || f.restaurantName.trim().length < 2)
      errs.restaurantName = t('errors.restaurantNameRequired');
    if (!f.password)
      errs.password = t('errors.passwordRequired');
    else if (f.password.length < 8 || !PASSWORD_REGEX.test(f.password))
      errs.password = t('errors.passwordWeak');
    if (!f.confirmPassword)
      errs.confirmPassword = t('errors.confirmPasswordRequired');
    else if (f.confirmPassword !== f.password)
      errs.confirmPassword = t('passwordMismatch');
    if (!f.terms)
      errs.terms = t('errors.termsRequired');
    return errs;
  };

  const errors = getErrors(form);
  const isFormValid = Object.keys(errors).length === 0;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setSubmitError('');
  };

  const handleBlur = (e) => {
    setTouched((prev) => ({ ...prev, [e.target.name]: true }));
  };

  const visibleError = (field) => (touched[field] ? errors[field] : '');

  const inputClass = (field) =>
    `w-full px-3.5 py-2.5 rounded-lg border bg-white text-gray-900 placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-orange-200 ${
      visibleError(field) ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-orange-500'
    }`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ fullName: true, email: true, phone: true, restaurantName: true, password: true, confirmPassword: true, terms: true });
    if (!isFormValid) return;

    setLoading(true);
    setSubmitError('');
    try {
      const payload = {
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phoneNumber: form.phone.trim(),
        restaurantName: form.restaurantName.trim(),
        password: form.password,
        subscribedPlan: plan,
        termsAccepted: form.terms,
      };
      const res = await apiClient.post('/api/auth/signup', payload);
      auth.login(res.data.token);
      navigate('/dashboard');
    } catch (err) {
      const data = err.response?.data;
      setSubmitError(data?.message || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 px-4 py-10">
      <div className="w-full max-w-md">
        {/* Brand header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-extrabold tracking-tight">
            <span style={{ color: '#085041' }}>Bento</span>
            <span className="text-orange-600">Ping</span>
          </h1>
          <p className="text-sm text-gray-600 mt-1">{t('common:tagline')}</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-orange-100/60 border border-orange-100 p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1">{t('title')}</h2>
              <p className="text-sm text-gray-500">{t('subtitle')}</p>
            </div>
            <LanguageSwitcher className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-600 bg-gray-50 hover:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-200 cursor-pointer" />
          </div>

          {plan && (
            <div className="mb-5 flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2.5 text-sm text-orange-800">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 shrink-0 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 0 0 1.946-.806 3.42 3.42 0 0 1 4.438 0 3.42 3.42 0 0 0 1.946.806 3.42 3.42 0 0 1 3.138 3.138 3.42 3.42 0 0 0 .806 1.946 3.42 3.42 0 0 1 0 4.438 3.42 3.42 0 0 0-.806 1.946 3.42 3.42 0 0 1-3.138 3.138 3.42 3.42 0 0 0-1.946.806 3.42 3.42 0 0 1-4.438 0 3.42 3.42 0 0 0-1.946-.806 3.42 3.42 0 0 1-3.138-3.138 3.42 3.42 0 0 0-.806-1.946 3.42 3.42 0 0 1 0-4.438 3.42 3.42 0 0 0 .806-1.946 3.42 3.42 0 0 1 3.138-3.138Z" />
              </svg>
              <span>
                <span className="font-semibold">{t('signup:selectedPlan', { plan })}</span>
                {trialDays != null && (
                  <>
                    {' · '}
                    <span>{t('signup:freeTrial', { days: trialDays })}</span>
                  </>
                )}
              </span>
            </div>
          )}

          {(submitError || googleError) && (
            <div role="alert" className="mb-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
              </svg>
              <span>{submitError || googleError}</span>
            </div>
          )}

          <div className="mb-5 flex justify-center">
            <GoogleSignInButton onCredential={handleGoogleCredential} disabled={googleLoading} />
          </div>

          <div className="mb-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-gray-200" />
            <span className="text-xs text-gray-400">{t('orSignUpWithEmail')}</span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            {/* Full name */}
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('fullName')}
              </label>
              <input
                id="fullName"
                name="fullName"
                type="text"
                autoComplete="name"
                value={form.fullName}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Jane Smith"
                disabled={loading}
                className={inputClass('fullName')}
              />
              {visibleError('fullName') && <p className="mt-1.5 text-xs text-red-600">{visibleError('fullName')}</p>}
            </div>

            {/* Work email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('email')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="you@restaurant.com"
                disabled={loading}
                className={inputClass('email')}
              />
              {visibleError('email') && <p className="mt-1.5 text-xs text-red-600">{visibleError('email')}</p>}
            </div>

            {/* Phone number */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('phoneNumber')}
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                value={form.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="+91 98765 43210"
                disabled={loading}
                className={inputClass('phone')}
              />
              {visibleError('phone') && <p className="mt-1.5 text-xs text-red-600">{visibleError('phone')}</p>}
            </div>

            {/* Restaurant name */}
            <div>
              <label htmlFor="restaurantName" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('restaurantName')}
              </label>
              <input
                id="restaurantName"
                name="restaurantName"
                type="text"
                value={form.restaurantName}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="My Restaurant"
                disabled={loading}
                className={inputClass('restaurantName')}
              />
              {visibleError('restaurantName') && <p className="mt-1.5 text-xs text-red-600">{visibleError('restaurantName')}</p>}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('password')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={form.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="••••••••"
                  disabled={loading}
                  className={`${inputClass('password')} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600 focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">{t('passwordHint')}</p>
              {visibleError('password') && <p className="mt-0.5 text-xs text-red-600">{visibleError('password')}</p>}
            </div>

            {/* Confirm password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('confirmPassword')}
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="••••••••"
                  disabled={loading}
                  className={`${inputClass('confirmPassword')} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((s) => !s)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600 focus:outline-none"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              {visibleError('confirmPassword') && <p className="mt-1.5 text-xs text-red-600">{visibleError('confirmPassword')}</p>}
            </div>

            {/* Terms */}
            <div>
              <label className="flex items-start gap-2.5 cursor-pointer select-none">
                <input
                  name="terms"
                  type="checkbox"
                  checked={form.terms}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={loading}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-orange-500 disabled:opacity-60"
                />
                <span className="text-sm text-gray-700">
                  {t('termsLabel')}{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline font-medium">
                    {t('termsLink')}
                  </a>
                  {' '}{t('termsAnd')}{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline font-medium">
                    {t('privacyLink')}
                  </a>
                </span>
              </label>
              {visibleError('terms') && <p className="mt-1.5 text-xs text-red-600">{visibleError('terms')}</p>}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || !isFormValid}
              className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Spinner />
                  {t('submitting')}
                </>
              ) : (
                t('submit')
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600">
            {t('haveAccount')}{' '}
            <Link to="/login" className="font-medium text-orange-600 hover:text-orange-700 hover:underline">
              {t('signIn')}
            </Link>
          </p>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6">
          {t('common:footer', { year: new Date().getFullYear() })}
        </p>
      </div>
    </div>
  );
}

export default SignupPage;